import { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, ListTodo, AlertCircle, Folder } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePublicTodoList, useCompletedTimeline } from '../hooks';
import type { PublicTodoListResponse } from '@/lib/api';

type TabValue = 'upcoming' | 'completed';
type PublicSection = PublicTodoListResponse['sections'][number];
type PublicItem = PublicTodoListResponse['items'][number];

/**
 * Unauthenticated public read of a Vault Todo list. The server already
 * stripped holder_id + audit columns, applied the inherited section gate and
 * the per-task hidden gate, and skipped hidden sections; the payload is a
 * minimal `PublicTodoListResponse`. The view is read-only — no Holder switcher,
 * no editor controls, no shared editor components (those expect the full Vault
 * payload and writability). Sub-sections render nested under their parent.
 */
export function PublicListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isCompletedView = location.pathname.endsWith('/completed');
  const activeTab: TabValue = isCompletedView ? 'completed' : 'upcoming';

  const { list, loading, error } = usePublicTodoList(slug!);
  const { items: completedItems, loading: completedLoading } = useCompletedTimeline({
    slug: slug!,
  });

  // Sections grouped by parent id (null = top level), each list pre-sorted.
  const sectionsByParent = useMemo(() => {
    const m = new Map<string | null, PublicSection[]>();
    if (!list) return m;
    for (const s of [...list.sections].sort((a, b) => a.sortOrder - b.sortOrder)) {
      const key = s.parentSectionId;
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [list]);

  // Active items grouped by their section id (null = unsorted). Completed items
  // never appear in the upcoming view (they live under the Completed tab).
  const itemsBySection = useMemo(() => {
    const m = new Map<string | null, PublicItem[]>();
    if (!list) return m;
    for (const item of list.items) {
      if (item.status === 'completed') continue;
      const arr = m.get(item.sectionId) ?? [];
      arr.push(item);
      m.set(item.sectionId, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return m;
  }, [list]);

  const unsortedItems = useMemo(() => itemsBySection.get(null) ?? [], [itemsBySection]);
  const topLevelSections = useMemo(() => sectionsByParent.get(null) ?? [], [sectionsByParent]);

  // A section is worth rendering if it (or any descendant) has an item — so a
  // parent that only holds sub-sections still shows, and a truly empty section
  // subtree is omitted rather than rendered as a bare heading.
  const sectionHasContent = useMemo(() => {
    const cache = new Map<string, boolean>();
    const check = (sectionId: string): boolean => {
      const cached = cache.get(sectionId);
      if (cached !== undefined) return cached;
      // Guard against a self-cache read mid-recursion (cyclic data): seed false.
      cache.set(sectionId, false);
      const hasItems = (itemsBySection.get(sectionId)?.length ?? 0) > 0;
      const children = sectionsByParent.get(sectionId) ?? [];
      const result = hasItems || children.some((c) => check(c.sectionId));
      cache.set(sectionId, result);
      return result;
    };
    return check;
  }, [itemsBySection, sectionsByParent]);

  const handleTabChange = (tab: TabValue) => {
    if (tab === 'completed') {
      navigate(`/a/arche_todo_lists/public/${slug}/completed`);
    } else {
      navigate(`/a/arche_todo_lists/public/${slug}`);
    }
  };

  const renderItem = (item: PublicItem) => (
    <div key={item.itemId} className="flex items-center gap-2 py-1 text-sm text-foreground">
      <span className="h-4 w-4 rounded border border-border flex-shrink-0" />
      <span className="truncate">{item.title}</span>
    </div>
  );

  // Recursively render a section subtree. Depth 0 renders as a Card; nested
  // sub-sections render as indented blocks inside it. `seen` guards against a
  // cyclic parent chain (the server rejects cycles on write, so this is pure
  // defense-in-depth — a bad row must never hang the public page).
  const renderSectionTree = (
    section: PublicSection,
    depth: number,
    seen: Set<string> = new Set(),
  ) => {
    if (seen.has(section.sectionId)) return null;
    if (!sectionHasContent(section.sectionId)) return null;
    const nextSeen = new Set(seen).add(section.sectionId);
    const directItems = itemsBySection.get(section.sectionId) ?? [];
    const childSections = sectionsByParent.get(section.sectionId) ?? [];

    const body = (
      <>
        {directItems.length > 0 && <div className="space-y-1">{directItems.map(renderItem)}</div>}
        {childSections.length > 0 && (
          <div className={`space-y-3 ${directItems.length > 0 ? 'mt-3' : ''}`}>
            {childSections.map((child) => renderSectionTree(child, depth + 1, nextSeen))}
          </div>
        )}
      </>
    );

    if (depth === 0) {
      return (
        <Card key={section.sectionId}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Folder className="h-4 w-4 text-muted-foreground" />
              {section.name}
            </CardTitle>
            {section.description && (
              <p className="text-xs text-muted-foreground">{section.description}</p>
            )}
          </CardHeader>
          <CardContent>{body}</CardContent>
        </Card>
      );
    }

    return (
      <div key={section.sectionId} className="ml-4 border-l border-border/60 pl-3">
        <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-1">
          <Folder className="h-3.5 w-3.5" />
          {section.name}
        </div>
        {section.description && (
          <p className="text-xs text-muted-foreground mb-1">{section.description}</p>
        )}
        {body}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Skeleton className="h-10 w-64 mb-3" />
          <Skeleton className="h-5 w-96 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error?.includes('not found') || !error ? 'Not Found' : 'Error'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error || 'This todo list could not be found or is no longer public.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasUpcomingContent =
    unsortedItems.length > 0 || topLevelSections.some((s) => sectionHasContent(s.sectionId));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-start gap-3 mb-2">
            <ListTodo className="h-7 w-7 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold">{list.name}</h1>
              {list.description && <p className="text-muted-foreground mt-1">{list.description}</p>}
            </div>
          </div>
        </div>
      </header>

      {/* Tab navigation */}
      <div className="border-b">
        <div className="max-w-3xl mx-auto px-4">
          <nav className="flex gap-6" aria-label="View mode">
            <button
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upcoming'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => handleTabChange('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === 'completed'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => handleTabChange('completed')}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Completed
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {activeTab === 'upcoming' ? (
            <div className="space-y-4">
              {topLevelSections.map((section) => renderSectionTree(section, 0))}

              {unsortedItems.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Unsorted
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">{unsortedItems.map(renderItem)}</CardContent>
                </Card>
              )}

              {!hasUpcomingContent && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No items to show.</p>
                </div>
              )}
            </div>
          ) : completedLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : completedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No completed items yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {completedItems.map((item) => (
                <div key={item.itemId} className="flex items-center gap-2 py-1 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-foreground truncate">{item.title}</span>
                  {item.completedAt && (
                    <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                      {new Date(item.completedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-6">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <a
            href="/"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Powered by Fias
          </a>
        </div>
      </footer>
    </div>
  );
}
